import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import TypewriterDemo from "@/components/TypewriterDemo";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  Sparkles, Brain, Mail, Users, Factory, Palette, Pencil, Send,
  CheckCircle, Clock, ArrowRight, Code, FileText, Layers, Calendar,
  Target, Zap, BarChart3, Database, Layout, Settings,
} from "lucide-react";

// ── Typewriter scenarios showing the actual multi-step pipeline ──

const SCENARIOS = [
  {
    text: "Create a 7-day Christmas sale campaign for a skincare brand",
    results: [
      { icon: <Brain size={14} className="text-purple-500" />, title: "Step 1 — Planning campaign structure", desc: "7 emails over 7 days: Teaser → Launch → Social Proof → Best Sellers → Urgency → Last Call → Thank You" },
      { icon: <Pencil size={14} className="text-blue-500" />, title: "Step 2 — Writing all 7 emails", desc: "Generating subject lines, preview text, body copy with {{first_name}} personalization, and CTAs for each step" },
      { icon: <Layout size={14} className="text-teal" />, title: "Step 3 — Assembling HTML templates", desc: "Each email wrapped in a responsive HTML template with brand colors, CTA buttons, and unsubscribe footer" },
      { icon: <Database size={14} className="text-amber-500" />, title: "Step 4 — Creating campaign records", desc: "7 draft campaigns + 1 sequence record created in your account. Each step linked to its campaign." },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Done — Ready to review and send", desc: "Open any email to edit, rearrange timing, or send immediately. Full control before anything goes out." },
    ],
  },
  {
    mistype: "Write a product lau",
    text: "3-email abandoned cart recovery sequence for an e-commerce store",
    results: [
      { icon: <Brain size={14} className="text-purple-500" />, title: "Step 1 — Planning recovery sequence", desc: "3 emails: Gentle Reminder (1hr) → Social Proof (24hr) → Final Urgency with 10% off (72hr)" },
      { icon: <Pencil size={14} className="text-blue-500" />, title: "Step 2 — Writing conversion copy", desc: "Cart recovery language, scarcity triggers, discount codes, and {{first_name}} personalization" },
      { icon: <Layout size={14} className="text-teal" />, title: "Step 3 — Building HTML emails", desc: "Responsive templates with product imagery placeholders, CTA buttons, and branded headers" },
      { icon: <Database size={14} className="text-amber-500" />, title: "Step 4 — Campaigns created", desc: "3 draft campaigns saved to your account with send timing pre-configured for each step" },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Done — Review and activate", desc: "Edit any email, adjust timing, then launch. Each email is a full campaign record you own." },
    ],
  },
  {
    text: "5-day SaaS onboarding welcome sequence for new trial users",
    results: [
      { icon: <Brain size={14} className="text-purple-500" />, title: "Step 1 — Designing onboarding arc", desc: "5 emails: Welcome → Feature Tour → Quick Win → Success Story → Trial Ending + Upgrade CTA" },
      { icon: <Pencil size={14} className="text-blue-500" />, title: "Step 2 — Generating onboarding content", desc: "Educational copy, feature highlights, social proof, and urgency. Each email has a distinct goal." },
      { icon: <Layout size={14} className="text-teal" />, title: "Step 3 — Assembling templates", desc: "Clean HTML with headings, body text, CTA buttons, and {{first_name}} greetings in every email" },
      { icon: <Database size={14} className="text-amber-500" />, title: "Step 4 — 5 campaigns created", desc: "Campaign records with subjects, HTML content, and send times saved as drafts. Sequence linked." },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Done — Onboarding ready", desc: "Review each step, edit copy, then activate. Day-by-day timing already set." },
    ],
  },
];

// ── Pipeline steps for the visual breakdown ──

const PIPELINE = [
  {
    num: "1",
    title: "AI Plans Your Campaign Structure",
    desc: "The AI reads your prompt, identifies the optimal number of emails (3–10), plans the timeline (which day each email sends), assigns email types (announcement, social proof, urgency, reminder, etc.), sets goals for each step, and chooses send times.",
    details: ["Campaign name and description generated", "Email types follow awareness → interest → desire → action arc", "Send times vary between 8 AM – 6 PM for realism", "Each email gets a distinct, non-repetitive goal"],
    icon: <Brain size={20} className="text-purple-500" />,
  },
  {
    num: "2",
    title: "AI Writes Every Email",
    desc: "For each step in the plan, a separate AI call generates the full email content — subject line, preview text, heading, body copy (2–4 paragraphs), and CTA button text. Each email is context-aware of the overall campaign arc.",
    details: ["Subject lines under 60 characters with {{first_name}} personalization", "Preview text complements (doesn't repeat) the subject", "Body copy matches your chosen tone and industry", "Personalization tokens: {{first_name}}, {{full_name}}, {{company}}"],
    icon: <Pencil size={20} className="text-blue-500" />,
  },
  {
    num: "3",
    title: "HTML Templates Assembled",
    desc: "Each email is wrapped in a responsive HTML template with your brand name, styled headings, formatted body paragraphs, a prominent CTA button linking to your URL, and a proper email footer with unsubscribe link.",
    details: ["Responsive design works in Gmail, Outlook, Apple Mail", "Brand name in header, CTA button with your URL", "Clean typography with proper paragraph spacing", "Unsubscribe footer included automatically"],
    icon: <Layout size={20} className="text-teal" />,
  },
  {
    num: "4",
    title: "Campaign Records Created in Your Account",
    desc: "The system creates real campaign records in your MoreMail account — one per email step, plus a parent sequence record linking them all. Each campaign is saved as a draft with its subject, HTML content, and preview text.",
    details: ["One campaign_sequences record (parent)", "One campaigns record per email step (draft status)", "One campaign_sequence_steps record per step with day, type, goal, and timing", "All wrapped in a database transaction — all or nothing"],
    icon: <Database size={20} className="text-amber-500" />,
  },
  {
    num: "5",
    title: "Review, Edit, and Send",
    desc: "Every generated email opens in your campaign editor. Edit subjects, body copy, timing, or CTAs. Rearrange the sequence. Then send immediately or schedule for later — each email is a standard campaign you fully control.",
    details: ["Click any email to open in the full campaign editor", "Edit subject, body, CTA, and send time", "Rearrange or delete steps from the sequence", "Send immediately or schedule — you're in control"],
    icon: <Send size={20} className="text-coral" />,
  },
];

export default function AiCampaignGeneratorLandingPage() {
  return (
    <>
      <SeoHead
        title="AI Campaign Generator — Full Email Sequences From a Single Prompt | MoreMail"
        description="Type one sentence. MoreMail's AI plans the campaign structure, writes every email with personalization, assembles HTML templates, creates campaign records, and sets send timing — all in under 30 seconds."
        canonical={`${SITE_URL}/ai-campaign-generator`}
        keywords="AI email campaign generator, AI email sequence builder, automated email writing, AI email marketing, email campaign from prompt, multi-step email generator, AI drip campaign"
        ogTitle="AI Campaign Generator — Complete Campaigns From One Prompt | MoreMail"
        ogDescription="Plan, write, build, and schedule a multi-step email campaign from a single sentence. AI handles everything."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "AI Campaign Generator", applicationCategory: "BusinessApplication", description: "Generate complete multi-step email campaigns from natural language prompts. AI plans the structure, writes every email, assembles HTML templates, and creates campaign records.", featureList: ["Natural language campaign prompts", "Multi-step sequence planning", "AI copywriting with personalization", "HTML email template assembly", "Campaign record creation", "8 industry templates", "5 tone options", "Send time scheduling"], offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />

      {/* Hero */}
      <section className="pt-12 pb-8 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
            <Sparkles size={13} className="text-coral" /> AI-Powered
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Full Email Campaigns<br />
            From a <em className="text-coral not-italic">Single Prompt</em>
          </h1>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Type one sentence. AI plans the campaign structure, writes every email with personalization, assembles HTML templates, creates campaign records in your account, and sets send timing — all in under 30 seconds.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/register"
              className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
              Start Free <ArrowRight size={15} />
            </Link>
            <a href="#pipeline"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold text-white/70 border border-white/20 hover:bg-white/10 transition-colors">
              See the Full Pipeline
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-sand">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "<30s", label: "Total generation time" },
              { value: "3–10", label: "Emails per campaign" },
              { value: "8", label: "Industry templates" },
              { value: "5", label: "Tone options" },
            ].map(s => (
              <div key={s.label}>
                <p className="font-display font-bold text-ink text-2xl">{s.value}</p>
                <p className="text-xs text-slate mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo */}
      <section className="py-10 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <TypewriterDemo scenarios={SCENARIOS} title="AI Campaign Generator" buttonLabel="Generate" />
        </div>
      </section>

      {/* Full Pipeline Breakdown */}
      <section id="pipeline" className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-3">
            The Complete <span className="text-coral">Pipeline</span>
          </h2>
          <p className="text-sm text-slate text-center mb-10 max-w-lg mx-auto">
            One prompt triggers a 5-step AI pipeline that plans, writes, builds, saves, and prepares your entire campaign. Here's exactly what happens.
          </p>

          <div className="space-y-8">
            {PIPELINE.map((step, i) => (
              <div key={i} className="flex gap-5">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 rounded-xl bg-paper border border-sand flex items-center justify-center">
                    {step.icon}
                  </div>
                  {i < PIPELINE.length - 1 && (
                    <div className="w-px flex-1 bg-sand mt-2" />
                  )}
                </div>
                <div className="pb-6">
                  <h3 className="font-display font-bold text-ink text-base mb-1">
                    <span className="text-coral mr-1.5">Step {step.num}.</span>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate leading-relaxed mb-3">{step.desc}</p>
                  <div className="bg-paper rounded-xl p-4 border border-sand">
                    <div className="space-y-1.5">
                      {step.details.map((d, j) => (
                        <div key={j} className="flex items-start gap-2 text-xs text-slate">
                          <CheckCircle size={12} className="text-teal flex-shrink-0 mt-0.5" />
                          <span>{d}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get — concrete outputs */}
      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            What You Get From <span className="text-coral">One Prompt</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Layers size={18} className="text-coral" />, title: "Multi-Step Campaign Plan", desc: "3–10 emails with logical progression: awareness → interest → desire → action. Each step has a distinct goal." },
              { icon: <FileText size={18} className="text-coral" />, title: "Complete Email Copy", desc: "Subject lines, preview text, body copy (2–4 paragraphs), and CTA button text — all written by AI for each step." },
              { icon: <Users size={18} className="text-coral" />, title: "Personalization Built In", desc: "Every email uses {{first_name}}, {{full_name}}, and {{company}} tokens. Greetings, body, and CTAs are personalized." },
              { icon: <Code size={18} className="text-coral" />, title: "Responsive HTML Templates", desc: "Each email assembled into a responsive HTML template with brand header, styled body, CTA button, and unsubscribe footer." },
              { icon: <Database size={18} className="text-coral" />, title: "Real Campaign Records", desc: "Every email is a real campaign in your account — not a preview. Edit, schedule, or send directly from your dashboard." },
              { icon: <Calendar size={18} className="text-coral" />, title: "Send Timing Pre-Set", desc: "Each step has a day number and send time already configured. Day 1 at 10am, Day 3 at 2pm — the AI picks optimal timing." },
              { icon: <Target size={18} className="text-coral" />, title: "Email Type Strategy", desc: "AI assigns types like announcement, social proof, urgency, reminder, and last call — following proven email marketing frameworks." },
              { icon: <Factory size={18} className="text-coral" />, title: "8 Industry Templates", desc: "E-commerce, SaaS, real estate, local services, education, agency, health, and general. Each shapes the AI's writing style." },
              { icon: <Palette size={18} className="text-coral" />, title: "5 Tone Options", desc: "Professional, friendly, urgent, playful, or casual. The tone affects every subject line, paragraph, and CTA across all emails." },
              { icon: <Settings size={18} className="text-coral" />, title: "Fully Editable Drafts", desc: "Every email opens in the campaign editor. Change subjects, rewrite body, swap CTAs, adjust timing — it's your campaign." },
              { icon: <BarChart3 size={18} className="text-coral" />, title: "Sequence Dashboard", desc: "View your entire campaign timeline — which emails send on which day, their status, and performance after sending." },
              { icon: <Zap size={18} className="text-coral" />, title: "Database Transaction", desc: "All campaign records are created in a single database transaction. Everything succeeds or nothing does — no partial campaigns." },
            ].map(b => (
              <div key={b.title} className="bg-white rounded-xl p-5 border border-sand">
                <div className="w-10 h-10 rounded-xl bg-paper flex items-center justify-center mb-3">{b.icon}</div>
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{b.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Under the Hood — technical details */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Under the <span className="text-coral">Hood</span>
          </h2>
          <div className="space-y-4">
            {[
              { q: "How does the AI plan the sequence?", a: "A planner AI call generates the campaign structure — campaign name, description, number of emails, which day each sends, email types (announcement, social proof, urgency, etc.), subject lines, and goals. The AI is instructed to follow the awareness → interest → desire → action framework." },
              { q: "How is each email written?", a: "Each email step gets its own dedicated AI call. The AI receives the overall campaign context plus the specific step's type, subject, and goal. This produces better quality than generating all emails in a single call. Each email gets a subject line, preview text, heading, 2–4 paragraphs of body copy, and CTA text." },
              { q: "What personalization tokens are used?", a: "{{first_name}} (falls back to 'there' if unknown), {{full_name}}, and {{company}} (from enrichment data). The AI is instructed to use {{first_name}} in the greeting of every email and {{company}} only when it fits naturally." },
              { q: "What happens in the database?", a: "One campaign_sequences record is created (the parent), one campaigns record per email step (each as a draft), and one campaign_sequence_steps record per step linking it to its campaign with day number, email type, goal, and send time. All wrapped in a transaction." },
              { q: "Can I edit after generation?", a: "Every email is a standard MoreMail campaign draft. Open it in the campaign editor to change the subject, rewrite body copy, adjust the CTA, swap the template, or change the send time. You can also delete steps or reorder the sequence." },
            ].map((item, i) => (
              <div key={i} className="bg-paper rounded-xl p-5 border border-sand">
                <h3 className="font-display font-bold text-ink text-sm mb-2">{item.q}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-10 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-lg font-bold text-ink text-center mb-5">More AI-Powered Features</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { href: "/auto-optimization", title: "Auto-Optimization", desc: "AI resends to non-openers" },
              { href: "/send-time-optimization", title: "Send-Time AI", desc: "Deliver at the perfect moment" },
              { href: "/engagement-scoring", title: "Engagement Scoring", desc: "Hot, warm & cold tiers" },
              { href: "/behavioral-triggers", title: "Behavioral Triggers", desc: "React to subscriber actions" },
              { href: "/revenue-attribution", title: "Revenue Attribution", desc: "Track revenue per email" },
              { href: "/list-hygiene", title: "List Hygiene", desc: "Auto-clean inactive contacts" },
              { href: "/prebuilt-campaigns", title: "Pre-Built Campaigns", desc: "20+ ready-to-send sequences" },
              { href: "/prebuilt-automations", title: "Pre-Built Automations", desc: "12+ install-and-go workflows" },
            ].map(link => (
              <Link key={link.href} to={link.href}
                className="bg-white rounded-xl p-3 border border-sand hover:border-coral/30 hover:shadow-sm transition-all">
                <h4 className="text-xs font-bold text-ink">{link.title}</h4>
                <p className="text-[10px] text-slate mt-0.5">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            One Sentence → Complete Campaign
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            Describe what you want. AI plans the sequence, writes every email, builds the HTML templates, creates campaign records in your account, and sets the timing. You review and send.
          </p>
          <div className="flex justify-center mb-6"><LeadCaptureBox /></div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
